import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CanvasSummary } from '@/api/types';
import { Dialog } from '@/app/layers/Dialog';
import { usePrefetchCanvasDocument } from '@/features/canvas/useCanvasDocument';
import { useCanvasActions, useCanvases } from '@/library/useCanvases';
import { useT, useTranslator } from '@/i18n/LocaleProvider';
import { cx } from '@/lib/cx';
import { formatExpiry, formatRelative } from '@/lib/relativeTime';
import styles from './Library.module.css';

/** Список канвасов (CNV-18): превью, «Default», участники, статус шаринга, меню Rename / Make default / Delete… */
export function CanvasesView({ onNewCanvas }: { onNewCanvas: () => void }) {
  const navigate = useNavigate();
  const t = useT();
  const { data: canvases = [] } = useCanvases();
  const { renameCanvas, deleteCanvas, setDefaultCanvas } = useCanvasActions();
  const [menuFor, setMenuFor] = useState<number | null>(null);
  const [renaming, setRenaming] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<CanvasSummary | null>(null);

  const open = (canvas: CanvasSummary) =>
    navigate(`/canvases/${canvas.id}`, { state: { from: 'list' } });

  return (
    <>
      <div className={cx(styles.head, styles.headCanvases)}>
        <div>
          <p className={styles.eyebrow}>{t('library.eyebrow')}</p>
          <h1 className={styles.title}>{t('library.canvasesTitle')}</h1>
          <p className={styles.lead}>{t('library.canvasesLead')}</p>
        </div>
        <button
          type="button"
          className={cx(styles.primary, styles.primaryLg)}
          data-tour="canvas-new"
          onClick={onNewCanvas}
        >
          {t('library.newCanvas')}
        </button>
      </div>

      <div className={styles.canvasGrid}>
        {canvases.map((canvas) => (
          <CanvasCard
            key={canvas.id}
            canvas={canvas}
            menuOpen={menuFor === canvas.id}
            renaming={renaming === canvas.id}
            onOpen={() => open(canvas)}
            onMenu={() => setMenuFor((v) => (v === canvas.id ? null : canvas.id))}
            onCloseMenu={() => setMenuFor(null)}
            onRename={() => {
              setMenuFor(null);
              setRenaming(canvas.id);
            }}
            onRenameDone={(name) => {
              setRenaming(null);
              const trimmed = name.trim();
              if (trimmed && trimmed !== canvas.name)
                void renameCanvas(canvas.id, trimmed, canvas.name);
            }}
            onMakeDefault={() => {
              setMenuFor(null);
              void setDefaultCanvas(canvas.id);
            }}
            onDelete={() => {
              setMenuFor(null);
              setConfirm(canvas);
            }}
          />
        ))}
      </div>

      <Dialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        labelledBy="canvas-delete-title"
        maxWidth={400}
        className={styles.confirmPanel}
      >
        <h3 id="canvas-delete-title" className={styles.confirmTitle}>
          {t('library.deleteTitle')}
        </h3>
        <p className={styles.confirmText}>{t('library.deleteText')}</p>
        <div className={styles.confirmActions}>
          <button type="button" className={styles.confirmCancel} onClick={() => setConfirm(null)}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className={styles.confirmDelete}
            onClick={() => {
              if (confirm) void deleteCanvas(confirm.id, confirm.nodes_count);
              setConfirm(null);
            }}
          >
            {t('common.delete')}
          </button>
        </div>
      </Dialog>
    </>
  );
}

interface CardProps {
  canvas: CanvasSummary;
  menuOpen: boolean;
  renaming: boolean;
  onOpen: () => void;
  onMenu: () => void;
  onCloseMenu: () => void;
  onRename: () => void;
  onRenameDone: (name: string) => void;
  onMakeDefault: () => void;
  onDelete: () => void;
}

function CanvasCard({
  canvas,
  menuOpen,
  renaming,
  onOpen,
  onMenu,
  onCloseMenu,
  onRename,
  onRenameDone,
  onMakeDefault,
  onDelete,
}: CardProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(canvas.name);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) onCloseMenu();
    };
    const onKey = (e: KeyboardEvent | globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onCloseMenu();
    };
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, onCloseMenu]);

  useEffect(() => {
    if (renaming) {
      setDraft(canvas.name);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [renaming, canvas.name]);

  const { t, tp } = useTranslator();
  const prefetch = usePrefetchCanvasDocument();
  const people = canvas.participants;
  const shown = people.slice(0, 3);
  const extra = people.length - shown.length;
  const share = canvas.share
    ? t('library.shared', {
        mode: canvas.share.mode === 'edit' ? t('share.modeEdit') : t('share.modeView'),
        status:
          canvas.share.status === 'active'
            ? t('library.shareExpires', { when: formatExpiry(canvas.share.expires_at) })
            : canvas.share.status === 'revoked'
              ? t('library.shareRevoked')
              : t('library.shareExpired'),
        views: tp('library.views', canvas.share.views_count),
      })
    : null;

  const onInputKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onRenameDone(draft);
    if (e.key === 'Escape') onRenameDone(canvas.name);
  };

  return (
    <article
      className={styles.canvasCard}
      data-canvas-id={canvas.id}
      onPointerEnter={() => prefetch(canvas.id)}
      onFocusCapture={() => prefetch(canvas.id)}
    >
      <button
        type="button"
        className={styles.preview}
        onClick={onOpen}
        aria-label={t('library.openCanvas', { name: canvas.name })}
      >
        {canvas.cover_url ? (
          <img src={canvas.cover_url} alt="" className={styles.previewCover} />
        ) : (
          <span className={styles.previewCover} />
        )}
        <span className={styles.previewNode} aria-hidden="true" />
        <span className={styles.previewLink} aria-hidden="true" />
        {canvas.is_default ? (
          <span className={styles.defaultBadge}>{t('library.default')}</span>
        ) : null}
      </button>
      <div className={styles.canvasBody} onClick={renaming ? undefined : onOpen}>
        <div className={styles.canvasRow}>
          {renaming ? (
            <input
              ref={inputRef}
              className={styles.renameInput}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => onRenameDone(draft)}
              onKeyDown={onInputKey}
              onClick={(e) => e.stopPropagation()}
              aria-label={t('canvas.name')}
              data-clarity-mask="true"
            />
          ) : (
            <h3 className={styles.canvasName} data-clarity-mask="true">
              {canvas.name}
            </h3>
          )}
          <button
            type="button"
            className={styles.more}
            title={t('library.more')}
            aria-label={t('library.moreActions', { name: canvas.name })}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={(e) => {
              e.stopPropagation();
              onMenu();
            }}
          >
            ···
          </button>
        </div>
        <div className={styles.canvasMeta}>
          <span className={styles.metaText}>
            {t('library.itemsUpdated', {
              items: tp('library.items', canvas.nodes_count),
              updated: formatRelative(canvas.updated_at),
            })}
          </span>
          <span className={styles.avatars} aria-label={tp('library.participants', people.length)}>
            {shown.map((p) => (
              <span key={p.user_id} className={styles.avatar} title={p.name}>
                {p.initials}
              </span>
            ))}
            {extra > 0 ? <span className={styles.avatar}>+{extra}</span> : null}
          </span>
        </div>
        {share ? (
          <div className={styles.shareLine}>
            <span className={styles.shareDot} aria-hidden="true" />
            {share}
          </div>
        ) : null}
      </div>
      {menuOpen ? (
        <div
          ref={menuRef}
          className={styles.menu}
          role="menu"
          aria-label={t('library.actionsFor', { name: canvas.name })}
        >
          <button type="button" role="menuitem" className={styles.menuItem} onClick={onRename}>
            {t('library.rename')}
          </button>
          {/* Default — один на пользователя: снимается только назначением другого канваса. */}
          {canvas.is_default ? null : (
            <button
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={onMakeDefault}
            >
              {t('library.makeDefault')}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className={cx(styles.menuItem, styles.menuDanger)}
            onClick={onDelete}
          >
            {t('library.deleteEllipsis')}
          </button>
        </div>
      ) : null}
    </article>
  );
}
